import {
  useEffect,
  useState
} from "react";

import api from "../api";

// ======================================================
// 📅 FORMAT DATE LOCALE
// ======================================================
function formatLocalDate(dateString) {
  if (!dateString) return "";

  const d = new Date(dateString);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

// ======================================================
// 💰 FORMAT MONNAIE
// ======================================================
function formatMoney(value) {
  return Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

// ======================================================
// 🏠 MODIFIER / GÉRER SÉJOUR
// ======================================================
function ModifierSejourModal({ data, onClose }) {
  const [nouvelleDate, setNouvelleDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null);

  const [facture, setFacture] = useState(null);
  const [loadingFacture, setLoadingFacture] = useState(true);
  const [factureErreur, setFactureErreur] = useState("");

  // ======================================================
  // 📅 AUJOURD'HUI
  // ======================================================
  const today = formatLocalDate(new Date());

  // ======================================================
  // 🔎 IDENTIFIANT DU SÉJOUR
  // ======================================================
  const sejourId = data?.sejour_id ?? data?.id;

  // ======================================================
  // 📊 SITUATION FINANCIÈRE = FACTURE
  // ======================================================
  const totalFacture = Number(facture?.montant_total || 0);
  const totalPayeFacture = Number(facture?.montant_paye || 0);
  const resteFacture = Number(facture?.reste_a_payer || 0);

  const factureSoldee = Boolean(
    facture &&
    resteFacture <= 0
  );

  // ======================================================
  // 📌 STATUT
  // ======================================================
  const statutNormalise = String(data?.statut || "").toLowerCase();

  // ======================================================
  // INIT + CHARGEMENT FACTURE
  // ======================================================
  useEffect(() => {
    if (!data?.id && !data?.sejour_id) {
      setFacture(null);
      setFactureErreur("Identifiant du séjour introuvable.");
      setLoadingFacture(false);
      return;
    }

    setNouvelleDate(
      formatLocalDate(data.date_fin_prevue)
    );

    const chargerFacture = async () => {
      setLoadingFacture(true);
      setFactureErreur("");

      try {
        const res = await api.get(
          `/factures/sejour/${sejourId}`
        );

        setFacture(
          res.data?.facture || res.data || null
        );
      } catch (err) {
        console.error(
          "Erreur chargement facture :",
          err
        );

        setFacture(null);
        setFactureErreur(
          err?.response?.status === 404
            ? "Aucune facture n'est associée à ce séjour."
            : "Impossible de charger la facture."
        );
      } finally {
        setLoadingFacture(false);
      }
    };

    chargerFacture();
  }, [data, sejourId]);

  // ======================================================
  // 📅 PROLONGER
  // ======================================================
  const handleModifier = async () => {
    if (!sejourId) {
      alert("Identifiant du séjour introuvable.");
      return;
    }

    if (!nouvelleDate) {
      alert("Choisissez une nouvelle date.");
      return;
    }

    if (nouvelleDate < today) {
      alert("Impossible de choisir une date passée.");
      return;
    }

    const dateFinActuelle = formatLocalDate(
      data?.date_fin_prevue
    );

    if (
      dateFinActuelle &&
      nouvelleDate <= dateFinActuelle
    ) {
      alert(
        "Pour prolonger le séjour, la nouvelle date doit être postérieure à la date de fin actuelle."
      );
      return;
    }

    if (!window.confirm("Confirmer la prolongation du séjour ?")) {
      return;
    }

    setLoading(true);
    setAction("modifier");

    try {
      await api.put(
        `/sejours/${sejourId}`,
        {
          date_fin_prevue: nouvelleDate
        }
      );

      alert("Séjour prolongé avec succès.");
      onClose();
    } catch (err) {
      console.error(
        "ERREUR PROLONGATION :",
        err
      );

      alert(
        err?.response?.data?.error ||
        "Erreur lors de la prolongation du séjour."
      );
    } finally {
      setLoading(false);
      setAction(null);
    }
  };

  // ======================================================
  // 🔚 TERMINER
  // ======================================================
  const handleTerminer = async () => {
    if (!sejourId) {
      alert("Identifiant du séjour introuvable.");
      return;
    }

    if (loadingFacture) {
      alert("Veuillez patienter pendant le chargement de la facture.");
      return;
    }

    if (!facture) {
      alert(
        factureErreur ||
        "Impossible de clôturer le séjour sans facture."
      );
      return;
    }

    if (!factureSoldee) {
      alert(
        `Impossible de terminer le séjour.\n\n` +
        `Paiement incomplet.\n` +
        `Reste à payer : ${formatMoney(resteFacture)} $`
      );
      return;
    }

    if (!window.confirm("Confirmer la clôture de ce séjour ?")) {
      return;
    }

    setLoading(true);
    setAction("terminer");

    try {
      const response = await api.post(
        `/sejours/terminer/${sejourId}`
      );

      const remboursement = Number(
        response?.data?.trop_percu || 0
      );

      if (remboursement > 0) {
        alert(
          `Séjour terminé avec succès.\n\n` +
          `Remboursement à effectuer : ${formatMoney(remboursement)} $`
        );
      } else {
        alert("Séjour terminé avec succès.");
      }

      onClose();
    } catch (err) {
      console.error(
        "ERREUR TERMINAISON SÉJOUR :",
        err
      );

      const error = err?.response?.data;

      if (error?.reste !== undefined) {
        alert(
          `Impossible de terminer le séjour.\n\n` +
          `Paiement incomplet.\n` +
          `Reste à payer : ${formatMoney(error.reste)} $`
        );
      } else {
        alert(
          error?.error ||
          "Erreur lors de la clôture du séjour."
        );
      }
    } finally {
      setLoading(false);
      setAction(null);
    }
  };

  // ======================================================
  // RENDER
  // ======================================================
  return (
    <div className="bg-white p-5 md:p-6 rounded-2xl shadow-2xl w-full max-w-lg mx-auto space-y-5">

      {/* ==================================================
          TITRE
      ================================================== */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-800">
          Gestion du séjour
        </h3>

        <p className="text-sm text-gray-500 mt-1">
          Prolonger ou clôturer le séjour
        </p>
      </div>

      {/* ==================================================
          INFORMATIONS
      ================================================== */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-2">

        {(data?.nom_client || data?.client) && (
          <div className="flex justify-between gap-3 text-sm">
            <span className="text-gray-500">Client</span>
            <span className="font-semibold text-gray-800 text-right">
              {data?.nom_client || data?.client}
            </span>
          </div>
        )}

        {data?.numero && (
          <div className="flex justify-between gap-3 text-sm">
            <span className="text-gray-500">Appartement</span>
            <span className="font-semibold text-gray-800">
              {data.numero}
            </span>
          </div>
        )}

        <div className="flex justify-between gap-3 text-sm">
          <span className="text-gray-500">Statut</span>
          <span
            className={
              statutNormalise === "expire"
                ? "font-semibold text-red-600"
                : "font-semibold text-indigo-600"
            }
          >
            {statutNormalise === "expire"
              ? "EXPIRÉ"
              : "EN COURS"}
          </span>
        </div>

        <div className="flex justify-between gap-3 text-sm">
          <span className="text-gray-500">Date d'arrivée</span>
          <span className="font-medium">
            {formatLocalDate(data?.date_debut)}
          </span>
        </div>

        <div className="flex justify-between gap-3 text-sm">
          <span className="text-gray-500">Fin prévue</span>
          <span className="font-medium">
            {formatLocalDate(data?.date_fin_prevue)}
          </span>
        </div>
      </div>

      {/* ==================================================
          SITUATION FINANCIÈRE = FACTURE
      ================================================== */}
      <div className="border rounded-xl p-4 space-y-3">
        <h4 className="font-semibold text-gray-800">
          Situation financière
        </h4>

        {loadingFacture ? (
          <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
            🔄 Chargement de la facture...
          </div>
        ) : facture ? (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total facture</span>
              <span className="font-semibold">
                {formatMoney(totalFacture)} $
              </span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total payé</span>
              <span className="font-semibold text-green-600">
                {formatMoney(totalPayeFacture)} $
              </span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Reste à payer</span>
              <span
                className={
                  resteFacture > 0
                    ? "font-semibold text-red-600"
                    : "font-semibold text-green-600"
                }
              >
                {formatMoney(resteFacture)} $
              </span>
            </div>

            {factureSoldee ? (
              <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium">
                ✓ Facture soldée
              </div>
            ) : (
              <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
                ⚠️ Paiement incomplet. Le séjour ne peut pas encore être clôturé.
              </div>
            )}
          </>
        ) : (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
            ⚠️ {factureErreur || "Facture introuvable."}
          </div>
        )}
      </div>

      {/* ==================================================
          PROLONGATION
      ================================================== */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nouvelle date de fin
        </label>

        <input
          type="date"
          value={nouvelleDate}
          min={today}
          onChange={(e) => setNouvelleDate(e.target.value)}
          disabled={loading}
          className="border px-4 py-3 rounded-xl w-full outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
        />

        <p className="text-xs text-gray-500 mt-2">
          Pour prolonger le séjour, choisissez une date postérieure à la date de fin actuelle.
        </p>
      </div>

      {/* ==================================================
          ACTIONS
      ================================================== */}
      <div className="flex flex-col md:flex-row gap-3">

        <button
          onClick={handleModifier}
          disabled={
            loading ||
            !nouvelleDate ||
            (
              data?.date_fin_prevue &&
              nouvelleDate <= formatLocalDate(data.date_fin_prevue)
            )
          }
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 transition text-white py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading && action === "modifier"
            ? "Prolongation..."
            : "📅 Prolonger"}
        </button>

        <button
          onClick={handleTerminer}
          disabled={
            loading ||
            loadingFacture ||
            !factureSoldee
          }
          className="flex-1 bg-green-600 hover:bg-green-700 transition text-white py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading && action === "terminer"
            ? "Clôture..."
            : "🔚 Terminer le séjour"}
        </button>
      </div>

      {/* ==================================================
          FERMER
      ================================================== */}
      <button
        onClick={onClose}
        disabled={loading}
        className="w-full bg-gray-200 hover:bg-gray-300 transition py-3 rounded-xl disabled:opacity-50"
      >
        Fermer
      </button>
    </div>
  );
}

export default ModifierSejourModal;
